import { RefObject, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Badge, InlineLink, SectionAlert, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { MarketingCharacteristics } from '@app/constants';
import { selectAddOns, selectSubscribedEasyCare } from '@app/redux/addOns';

import { ProductType } from '@mfe/shared/schema-types';

import SubscribedEasyCareProduct from './easy-care-subscribed-item';
import OffersList from './offer-list';
import { getDescription } from '../../utils';

import styles from '../../styles/grouped-addon.module.scss';

export const EasyCareSelector = ({
  missingSelectionRef,
  currentShopAddon,
}: {
  missingSelectionRef: RefObject<HTMLDivElement>;
  currentShopAddon: ProductType | null;
}) => {
  const { t } = useTranslation('ShopAddons');

  const { isMissingEasyCareSelection } = useSelector(selectAddOns);
  const subscribedEasyCareProduct = useSelector(selectSubscribedEasyCare);

  useEffect(() => {
    const element = missingSelectionRef.current;
    if (element && isMissingEasyCareSelection) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isMissingEasyCareSelection, missingSelectionRef]);

  if (!currentShopAddon) {
    return null;
  }

  const description = getDescription(currentShopAddon);

  const { package_types, marketing_copy: { translations = [] } = {} } = currentShopAddon;

  if (!package_types || package_types.length === 0) {
    return null;
  }

  const characteristics = translations[0]?.characteristics ?? [];

  const tag =
    characteristics.find((characteristic) => characteristic.name === MarketingCharacteristics.TAG)?.value ?? null;

  const currentProductTypes = package_types[0].product_types || [];

  if (currentProductTypes?.length === 0) {
    return null;
  }

  const products = currentProductTypes.filter((product) => product?.id !== subscribedEasyCareProduct?.productTypeId);

  if (products && products.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles['tag']}>{tag && <Badge label={tag} mr={'24px'} mt={'24px'} />}</div>
      <div className={styles['title-and-selector']}>
        <div className={styles['title-subtitle']}>
          <Txt variant="heading4" mb="16px">
            {t('groupedEasyCare.cardTitle')}
          </Txt>
          <Txt component="span" variant="paragraphRegular">
            {description ?? t('groupedEasyCare.cardDescription')}
          </Txt>
        </div>

        <div className={styles['grouped-selector']}>
          <Txt variant="bodySmallRegular" color="subtle">
            {t('groupedEasyCare.rightTier')}
            <InlineLink
              ml="4px"
              href=""
              onClick={(e) => {
                e.preventDefault();
                const comparisonTable = document.getElementById('tier-features-table');

                if (comparisonTable) {
                  comparisonTable.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {t('groupedEasyCare.compareTierFeatures')}
            </InlineLink>
          </Txt>

          {isMissingEasyCareSelection && (
            <div ref={missingSelectionRef}>
              <SectionAlert
                id="no-add-on-selected"
                variant="error"
                label={t('groupedEasyCare.missingSelectionAlert')}
              />
            </div>
          )}

          <div className={styles['selector-options']}>
            {subscribedEasyCareProduct && <SubscribedEasyCareProduct product={subscribedEasyCareProduct} />}
            {products && <OffersList offers={products.filter((p): p is ProductType => p != null)} />}
          </div>
        </div>
      </div>
    </>
  );
};
